/**
 * The page is imported from **its own module**, not from the `@pages` barrel.
 *
 * The barrel re-exports every page, so a route importing it pulls all of them into one shared chunk
 * that the entry then preloads — code-splitting by route, defeated by one import.
 */
import { createFileRoute } from '@tanstack/react-router';

import { SharedI18n } from '@shared';

import { AssistantPage } from '@pages/assistant';
import { AssistantModel } from '@units/assistant';
import { IamService } from '@units/iam';

/**
 * `/assistant` — wiring only (`rules/frontend-fsd.mdc` rule 10).
 *
 * The assistant, which answers only with a citation and otherwise says it does not know.
 *
 * The screen behind it is the real one — a conversation with history, visible conditions and an
 * attachment (EPIC-042, closed 19 August 2026) — and the guard is what stands in front of it:
 * somebody without `ai:use` is stopped here, before anything renders. The docstring claimed a
 * placeholder until 2026-08-19, long after the placeholder was gone; a route file that describes
 * the screen it does not contain is exactly the claim `stale-claims-auditor` exists to catch.
 */
export const Route = createFileRoute('/_authenticated/assistant')({
  beforeLoad: IamService.IamGuards.requirePermission({
    permission: 'ai:use',
    // The section is in the navigation of everybody who holds the right to it, so its existence is
    // no secret inside the organization and the refusal may name what is missing. The documents
    // *inside* it are the closed contour, and answer 404 (`rules/permissions.mdc`).
    whenDenied: 'forbidden',
  }),
  /*
   * Открытый разговор живёт в адресе (`?thread=`), а не в памяти вкладки.
   *
   * До истории разговоров экран открывался пустым после каждой перезагрузки, «назад» уводил со
   * страницы вместо предыдущего разговора, а переслать разговор коллеге было нечем. Схема разбирает
   * параметр и отбрасывает мусор, не падая: упавший `validateSearch` подменил бы весь экран
   * error-boundary'ем на присланной ссылке (`rules/lists-and-filters.mdc`, §1–2).
   */
  validateSearch: AssistantModel.assistantSearchSchema,
  /*
   * Русский словарь разговора приезжает вместе с экраном, а не с первой загрузкой.
   *
   * `assistant.json` — 5,28 кБ gzip, самый крупный словарь, который спрашивает ровно один маршрут:
   * ключи `assistant.*` не встречаются нигде, кроме `pages/assistant` и `units/assistant`, а сам
   * юнит не импортирует никто, кроме этого файла и его страницы. Ждать его здесь, а не внутри
   * дерева, — потому что роутер не отрисует маршрут, пока `loader` не закончил: догрузка в
   * компоненте дала бы первый кадр сырыми ключами с заменой текста на глазах.
   *
   * Своих `pendingComponent`/`errorComponent` маршруту от этого не нужно — рисовать нечего, ждать
   * нечего, границы наследуются от роутера.
   */
  loader: () => SharedI18n.loadCatalogue('assistant'),
  component: AssistantPage,
  staticData: { crumbKey: 'nav.assistant' },
});
